import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, ArrowLeft, CheckCircle2, Building2 } from 'lucide-react';
import { useAuth, formatApiErrorDetail } from '../context/AuthContext';
import api from '../lib/api';

export default function AnuncianteCadastro() {
  const navigate = useNavigate();
  const { registerAnunciante } = useAuth();
  const [form, setForm] = useState({
    nomeFantasia: '', razaoSocial: '', cnpj: '',
    email: '', senha: '',
    whatsapp: '', site: '',
    tipoEmpresa: '',
    cep: '', rua: '', numero: '', complemento: '', bairro: '',
    cidade: '', estado: '',
  });
  const [tipos, setTipos] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/tipos-empresa').then(r => setTipos(r.data)).catch(() => {});
    api.get('/cidades').then(r => setCidades(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onCidade = (id) => {
    const c = cidades.find(x => x.id === id);
    if (c) setForm(f => ({ ...f, cidade: c.nome, estado: c.uf }));
  };

  const onCepBlur = async () => {
    const cep = form.cep.replace(/\D/g, '');
    if (cep.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const d = await res.json();
      if (!d.erro) {
        setForm(f => ({
          ...f,
          rua: d.logradouro || f.rua,
          bairro: d.bairro || f.bairro,
          cidade: d.localidade || f.cidade,
          estado: d.uf || f.estado,
        }));
      }
    } catch { /* ignore */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErro(''); setLoading(true);
    try {
      await registerAnunciante(form);
      navigate('/anunciante/dashboard');
    } catch (err) {
      setErro(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Link to="/" className="flex items-center gap-2 px-4 py-4 text-sm text-gray-600 hover:text-[#1D9E75]">
        <ArrowLeft size={15} /> Voltar
      </Link>
      <div className="flex-1 flex items-center justify-center px-4 py-6">
        <div className="w-full max-w-xl bg-white rounded-2xl shadow-sm p-7">
          <div className="mb-5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#E1F5EE] flex items-center justify-center text-[#1D9E75]">
              <Building2 size={18} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Work Sans', sans-serif" }}>Anunciante</h1>
              <p className="text-sm text-gray-500">Crie sua conta · 60 dias grátis</p>
            </div>
          </div>

          <div className="bg-[#E1F5EE] border border-[#1D9E75]/20 rounded-xl px-3 py-2.5 mb-4 flex items-start gap-2">
            <CheckCircle2 size={14} className="text-[#1D9E75] mt-0.5 flex-shrink-0" />
            <p className="text-xs text-[#0F6E56]">
              <strong>60 dias grátis</strong> de plataforma. Após o período, R$ 79,90/mês (cartão).
              O 1º anúncio é grátis por 14 dias.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Nome fantasia *</label>
                <input required value={form.nomeFantasia} onChange={set('nomeFantasia')} data-testid="anunc-reg-nome-input" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Razão social</label>
                <input value={form.razaoSocial} onChange={set('razaoSocial')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">CNPJ</label>
                <input value={form.cnpj} onChange={set('cnpj')} placeholder="00.000.000/0000-00" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Tipo de empresa</label>
                <select value={form.tipoEmpresa} onChange={set('tipoEmpresa')} data-testid="anunc-reg-tipo-select" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1D9E75]">
                  <option value="">— Selecione —</option>
                  {tipos.map(t => <option key={t.id} value={t.nome}>{t.nome}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Email *</label>
                <input type="email" required value={form.email} onChange={set('email')} data-testid="anunc-reg-email-input" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Senha (mín. 6) *</label>
                <input type="password" required minLength={6} value={form.senha} onChange={set('senha')} data-testid="anunc-reg-senha-input" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">WhatsApp</label>
                <input value={form.whatsapp} onChange={set('whatsapp')} placeholder="(11) 99999-0000" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Site</label>
                <input value={form.site} onChange={set('site')} placeholder="https://" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">CEP</label>
                <input value={form.cep} onChange={set('cep')} onBlur={onCepBlur} placeholder="00000-000" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 mb-1">Rua</label>
                <input value={form.rua} onChange={set('rua')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Número</label>
                <input value={form.numero} onChange={set('numero')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Complemento</label>
                <input value={form.complemento} onChange={set('complemento')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Bairro</label>
                <input value={form.bairro} onChange={set('bairro')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Cidade</label>
              <select onChange={(e) => onCidade(e.target.value)} data-testid="anunc-reg-cidade-select" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1D9E75]">
                <option value="">— {form.cidade || 'Selecione'} —</option>
                {cidades.map(c => <option key={c.id} value={c.id}>{c.nome} / {c.uf}</option>)}
              </select>
            </div>

            {erro && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">{erro}</div>}

            <button type="submit" disabled={loading} data-testid="anunc-reg-submit-btn"
              className="w-full bg-[#1D9E75] hover:bg-[#178460] text-white font-medium rounded-xl py-2.5 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 mt-2">
              {loading && <Loader2 size={14} className="animate-spin" />} Criar conta grátis
            </button>
          </form>

          <p className="text-center text-xs text-gray-500 mt-6">
            Já tem conta? <Link to="/anunciante/entrar" className="text-[#1D9E75] font-semibold hover:underline">Entrar</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
